/**
 * Classical computer-vision tennis-ball tracker — no ML model required.
 *
 * Pipeline per frame (all arithmetic on a down-sampled working canvas):
 *   1. Downsample the input frame to ~`inputScale` px wide via OffscreenCanvas.
 *   2. Absolute-difference vs. the previous down-sampled frame → binary
 *      motion mask (threshold 25/255 on grayscale difference).
 *   3. HSV colour filter keeping H ∈ [35°, 75°], S > 0.35, V > 0.45 → yellow
 *      mask. Tennis balls are canonical yellow-green; grass passes too but
 *      is filtered out by the motion mask.
 *   4. Intersect the motion and colour masks.
 *   5. Connected-components labelling (two-pass union-find) over the
 *      intersection → candidate blobs. Reject anything outside
 *      [`minAreaPx`, `maxAreaPx`].
 *   6. Kalman gating: predict next position from the last state; pick the
 *      survivor nearest to the prediction within a normalised Euclidean
 *      gate of ~0.12. If no survivor clears the gate, return `null` but
 *      keep predicting for up to `MAX_COAST_FRAMES` — this covers brief
 *      occlusions (racket, player body, backcourt fence).
 *   7. Feed the chosen centroid back into the Kalman filter; return the
 *      normalised camera-space coordinate with a confidence derived from
 *      blob area.
 *
 * Dependencies: OffscreenCanvas + ImageData + the in-repo `Kalman2D`. Zero
 * npm adds. Gracefully no-ops (returns `null`) on browsers without
 * OffscreenCanvas.
 */
import { Kalman2D } from '@tbc/core/tracking';

export interface BallCandidate {
  readonly x: number;
  readonly y: number;
  readonly confidence: number;
}

export interface ClassicalBallTrackerOptions {
  /** Target working-canvas width in pixels. Defaults to 240. */
  readonly inputScale?: number;
  /**
   * Minimum accepted blob area, in *working-canvas* pixels. Too small and
   * single-pixel noise spikes leak through. Default 3.
   */
  readonly minAreaPx?: number;
  /**
   * Maximum accepted blob area, in working-canvas pixels. Too large and
   * player shirts / shadows win. Default 400.
   */
  readonly maxAreaPx?: number;
}

interface Blob {
  readonly cx: number; // normalised [0,1]
  readonly cy: number; // normalised [0,1]
  readonly area: number; // working-canvas pixels
}

// Kalman gate radius in normalised coordinates. 0.12 ≈ 12% of the frame
// diagonal — generous enough to cover a fast ball between ~15 FPS ticks at
// realistic clip speeds, tight enough to reject most decoys.
const KALMAN_GATE = 0.12;

// How many consecutive "no match" frames we allow the Kalman filter to keep
// predicting before we treat the track as lost and require a re-lock. 8
// frames at ~15 FPS ≈ half a second — a typical ball-behind-racket occlusion.
const MAX_COAST_FRAMES = 8;

// Motion / colour mask thresholds — tuned on daylight hard-court footage.
const MOTION_THRESHOLD = 25; // 0..255, on grayscale abs-diff
const HUE_MIN_DEG = 35;
const HUE_MAX_DEG = 75;
const SAT_MIN = 0.35;
const VAL_MIN = 0.45;

export class ClassicalBallTracker {
  private readonly inputScale: number;
  private readonly minAreaPx: number;
  private readonly maxAreaPx: number;

  private canvas: OffscreenCanvas | null = null;
  private ctx: OffscreenCanvasRenderingContext2D | null = null;
  private prevGray: Uint8ClampedArray | null = null;
  private canvasW = 0;
  private canvasH = 0;

  private readonly kalman: Kalman2D;
  private hasLock = false;
  private coastFrames = 0;
  private lastTimestampMs: number | null = null;
  private readonly disabled: boolean;

  constructor(opts: ClassicalBallTrackerOptions = {}) {
    this.inputScale = opts.inputScale ?? 240;
    this.minAreaPx = opts.minAreaPx ?? 3;
    this.maxAreaPx = opts.maxAreaPx ?? 400;
    this.kalman = new Kalman2D({
      // Per-frame dt refined at ingest time; default here is fine for warm-up.
      dt: 1 / 15,
      // Ball trajectories change hard and fast (racket impact); trust
      // measurements more than the prototype defaults.
      processNoise: 5e-3,
      measurementNoise: 5e-3,
      initialCovariance: 1,
    });

    // Gracefully disable on legacy browsers without OffscreenCanvas.
    this.disabled = typeof OffscreenCanvas === 'undefined';
  }

  /**
   * Feed one frame. Returns the tracked ball position (normalised camera
   * coords, `x,y ∈ [0,1]`) with a `confidence ∈ [0,1]`, or `null` when no
   * ball is visible (or the tracker is disabled on this platform).
   */
  ingest(
    frame: HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
    timestampMs: number,
  ): BallCandidate | null {
    if (this.disabled) return null;

    const gray = this.drawAndGrayscale(frame);
    if (!gray) return null; // first-ever call: no prior frame, no motion mask

    const blobs = this.extractBlobs(gray.gray, gray.rgba);

    // Determine dt so the Kalman step size matches reality regardless of the
    // caller's FPS (TestVideo throttles to ~15 Hz; a recording route may hit
    // 60 Hz).
    const dt = this.lastTimestampMs == null
      ? 1 / 15
      : Math.max(1 / 120, Math.min(1, (timestampMs - this.lastTimestampMs) / 1000));
    this.lastTimestampMs = timestampMs;

    if (blobs.length === 0) {
      return this.coastOrLose(dt);
    }

    if (!this.hasLock) {
      // Cold start: pick the largest blob as the seed. This can mis-lock on
      // a linesperson's shirt in the very first frame; the motion-filter
      // generally saves us by the second frame.
      let best = blobs[0]!;
      for (const b of blobs) if (b.area > best.area) best = b;
      this.kalman.reset({ x: best.cx, y: best.cy, vx: 0, vy: 0 }, 1);
      this.hasLock = true;
      this.coastFrames = 0;
      return { x: best.cx, y: best.cy, confidence: this.confidenceFromArea(best.area) };
    }

    // We have a lock → predict & gate.
    this.kalman.predict(dt);
    const pred = this.kalman.getState();

    let best: Blob | null = null;
    let bestDist = KALMAN_GATE;
    for (const b of blobs) {
      const d = Math.hypot(b.cx - pred.x, b.cy - pred.y);
      if (d < bestDist) {
        bestDist = d;
        best = b;
      }
    }

    if (!best) {
      return this.coastOrLose(0); // predict() already done above
    }

    const s = this.kalman.update(best.cx, best.cy);
    this.coastFrames = 0;
    return { x: s.x, y: s.y, confidence: this.confidenceFromArea(best.area) };
  }

  /** Drop the current track — next call will cold-start. */
  reset(): void {
    this.hasLock = false;
    this.coastFrames = 0;
    this.prevGray = null;
    this.lastTimestampMs = null;
  }

  /** Release the OffscreenCanvas reference. Idempotent. */
  dispose(): void {
    this.reset();
    this.canvas = null;
    this.ctx = null;
  }

  // --- private helpers ---

  /**
   * Either predict-only (still within coast budget) or declare track lost.
   * Returns the coasting position so the UI can show a "last known" ghost.
   */
  private coastOrLose(dt: number): BallCandidate | null {
    if (!this.hasLock) return null;
    if (dt > 0) this.kalman.predict(dt);
    this.coastFrames += 1;
    if (this.coastFrames > MAX_COAST_FRAMES) {
      this.hasLock = false;
      return null;
    }
    const s = this.kalman.getState();
    // Clamp inside [0,1] — the filter can drift past edges during long coasts.
    if (s.x < 0 || s.x > 1 || s.y < 0 || s.y > 1) return null;
    return { x: s.x, y: s.y, confidence: 0 };
  }

  /**
   * Draw the frame to the internal OffscreenCanvas at `inputScale` width,
   * return its grayscale + rgba views. On the first-ever call the previous
   * frame's grayscale is seeded but no motion mask exists yet → returns null.
   */
  private drawAndGrayscale(
    frame: HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
  ): { gray: Uint8ClampedArray; rgba: Uint8ClampedArray } | null {
    const srcW = 'videoWidth' in frame
      ? frame.videoWidth
      : 'width' in frame
        ? frame.width
        : 0;
    const srcH = 'videoHeight' in frame
      ? frame.videoHeight
      : 'height' in frame
        ? frame.height
        : 0;
    if (srcW <= 0 || srcH <= 0) return null;

    const scale = Math.min(1, this.inputScale / srcW);
    const w = Math.max(16, Math.round(srcW * scale));
    const h = Math.max(16, Math.round(srcH * scale));

    if (!this.canvas || this.canvasW !== w || this.canvasH !== h) {
      this.canvas = new OffscreenCanvas(w, h);
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      this.canvasW = w;
      this.canvasH = h;
      this.prevGray = null; // invalidate prior grayscale on resize
    }
    if (!this.ctx) return null;

    this.ctx.drawImage(frame as CanvasImageSource, 0, 0, w, h);
    const img = this.ctx.getImageData(0, 0, w, h);
    const rgba = img.data;

    const gray = new Uint8ClampedArray(w * h);
    for (let i = 0, p = 0; p < rgba.length; i++, p += 4) {
      // Rec-601 luma — cheap and good enough for diffing.
      gray[i] = (rgba[p]! * 0.299 + rgba[p + 1]! * 0.587 + rgba[p + 2]! * 0.114) | 0;
    }

    if (!this.prevGray) {
      // First frame ever — seed and bail. Nothing to diff against.
      this.prevGray = gray;
      return null;
    }

    return { gray, rgba };
  }

  /**
   * Produce the motion ∩ yellow mask and run connected-components over it.
   * After this method returns, `prevGray` is rotated to the current frame.
   */
  private extractBlobs(gray: Uint8ClampedArray, rgba: Uint8ClampedArray): Blob[] {
    const w = this.canvasW;
    const h = this.canvasH;
    const prev = this.prevGray!;
    const mask = new Uint8Array(w * h);

    for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
      const diff = Math.abs(gray[i]! - prev[i]!);
      if (diff < MOTION_THRESHOLD) continue;
      if (!isYellowish(rgba[p]!, rgba[p + 1]!, rgba[p + 2]!)) continue;
      mask[i] = 1;
    }

    // Rotate the previous-frame buffer now that we're done reading it.
    this.prevGray = gray;

    return this.connectedComponents(mask, w, h);
  }

  /**
   * Two-pass connected-components labelling with 4-neighbour connectivity
   * and union-find. Keeps only blobs whose area is in range. Returns blobs
   * with centroids already normalised to [0,1] against the working canvas.
   */
  private connectedComponents(mask: Uint8Array, w: number, h: number): Blob[] {
    // labels[i] = 0 → background; otherwise → component id (1-based).
    const labels = new Int32Array(mask.length);
    const parent: number[] = [0];
    let nextId = 1;

    const find = (a: number): number => {
      let r = a;
      while (parent[r] !== r) r = parent[r]!;
      // Path compression.
      let c = a;
      while (parent[c] !== r) {
        const next = parent[c]!;
        parent[c] = r;
        c = next;
      }
      return r;
    };
    const union = (a: number, b: number): void => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[rb < ra ? ra : rb] = rb < ra ? rb : ra;
    };

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (!mask[i]) continue;
        const up = y > 0 ? labels[i - w] : 0;
        const left = x > 0 ? labels[i - 1] : 0;
        if (!up && !left) {
          labels[i] = nextId;
          parent[nextId] = nextId;
          nextId += 1;
        } else if (up && !left) {
          labels[i] = up;
        } else if (!up && left) {
          labels[i] = left;
        } else {
          // Both — merge.
          labels[i] = up!;
          if (up !== left) union(up!, left!);
        }
      }
    }

    // Aggregate area and centroids per *root* label.
    const stats = new Map<number, { area: number; sumX: number; sumY: number }>();
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const l = labels[i];
        if (!l) continue;
        const r = find(l);
        let s = stats.get(r);
        if (!s) {
          s = { area: 0, sumX: 0, sumY: 0 };
          stats.set(r, s);
        }
        s.area += 1;
        s.sumX += x;
        s.sumY += y;
      }
    }

    const blobs: Blob[] = [];
    for (const s of stats.values()) {
      if (s.area < this.minAreaPx || s.area > this.maxAreaPx) continue;
      blobs.push({
        cx: (s.sumX / s.area) / (w - 1 || 1),
        cy: (s.sumY / s.area) / (h - 1 || 1),
        area: s.area,
      });
    }
    return blobs;
  }

  private confidenceFromArea(area: number): number {
    // Map area to [0,1] with 25 px as the "definitely a ball" knee.
    return Math.max(0, Math.min(1, area / 25));
  }
}

/**
 * HSV gate in RGB space. We skip the full HSV conversion and test only the
 * pieces of HSV we need: hue in the yellow-green band, enough saturation,
 * enough value. This is ~3× faster than computing real HSV per pixel.
 */
function isYellowish(r: number, g: number, b: number): boolean {
  const maxC = Math.max(r, g, b);
  const minC = Math.min(r, g, b);
  const v = maxC / 255;
  if (v < VAL_MIN) return false;
  const d = maxC - minC;
  if (d === 0) return false; // gray pixel, no hue
  const s = d / maxC;
  if (s < SAT_MIN) return false;

  // Compute hue (degrees) from the dominant channel.
  let h: number;
  if (maxC === r) {
    h = 60 * (((g - b) / d) % 6);
  } else if (maxC === g) {
    h = 60 * ((b - r) / d + 2);
  } else {
    h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return h >= HUE_MIN_DEG && h <= HUE_MAX_DEG;
}
